import { Link } from "@tanstack/react-router";
import { Paper, Image, Group, Stack, Text, Badge, Anchor, Box, ActionIcon } from "@mantine/core";
import { IconMapPin, IconResize, IconHome, IconStar, IconStarFilled } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apartmentsApi } from "@/api/client";
import { StatusBadge, PriceBadge } from "@/components/common";
import type { Apartment, PriceChange } from "@/api/types";

interface ApartmentCardProps {
  apartment: Apartment;
  priceChange?: PriceChange;
}

export function ApartmentCard({ apartment, priceChange }: ApartmentCardProps) {
  const queryClient = useQueryClient();
  const imageUrl = apartment.images?.[0];

  const toggleFavorite = useMutation({
    mutationFn: () => apartmentsApi.toggleFavorite(apartment.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apartments"] });
    },
  });

  return (
    <Paper p="md" radius="md" withBorder style={{ borderColor: "#0f3460" }}>
      {imageUrl ? (
        <Image src={imageUrl} alt={apartment.title} h={192} radius="md" mb="sm" fit="cover" />
      ) : (
        <Box
          h={192}
          mb="sm"
          style={{
            backgroundColor: "#0f3460",
            borderRadius: "0.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text c="dimmed">No image</Text>
        </Box>
      )}

      <Group justify="space-between" align="flex-start" gap="xs" mb="xs">
        <Stack gap={4} style={{ flex: 1 }}>
          <Text size="lg" fw={600}>
            <Anchor
              component={Link}
              to="/apartments/$id"
              params={{ id: apartment.id.toString() }}
              c="#eeeeee"
              underline="never"
            >
              {apartment.title}
            </Anchor>
          </Text>
          {apartment.is_corporate && (
            <Badge size="xs" color="blue" variant="light">
              Corporate
            </Badge>
          )}
        </Stack>
        <Group gap="xs">
          <ActionIcon
            variant="subtle"
            color={apartment.is_favorite ? "yellow" : "gray"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleFavorite.mutate();
            }}
            loading={toggleFavorite.isPending}
          >
            {apartment.is_favorite ? <IconStarFilled size={20} /> : <IconStar size={20} />}
          </ActionIcon>
          <StatusBadge status={apartment.status} />
        </Group>
      </Group>

      <Group gap="md" mb="sm" wrap="wrap">
        {apartment.address && (
          <Group gap={4}>
            <IconMapPin size={16} color="#888888" />
            <Text size="sm" c="dimmed">
              {apartment.address}
            </Text>
          </Group>
        )}
        {apartment.size_sqm && (
          <Group gap={4}>
            <IconResize size={16} color="#888888" />
            <Text size="sm" c="dimmed">
              {apartment.size_sqm} m²
            </Text>
          </Group>
        )}
        {apartment.rooms && (
          <Group gap={4}>
            <IconHome size={16} color="#888888" />
            <Text size="sm" c="dimmed">
              {apartment.rooms} rooms
            </Text>
            {apartment.is_first_hand && (
              <Badge size="xs" color="green" variant="light">
                First hand
              </Badge>
            )}
          </Group>
        )}
      </Group>

      <Group>
        <Text size="xl" fw={700} c="primary">
          {apartment.rent.toLocaleString()} kr/mo
        </Text>
        {priceChange && (
          <PriceBadge
            direction={
              Number(priceChange.new_value) < Number(priceChange.old_value) ? "down" : "up"
            }
          />
        )}
      </Group>
    </Paper>
  );
}
